import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatCommissionsLevelName, formatLocaleDate } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Dropdown, Popconfirm, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useDeleteCommissionPromotion,
  useDownloadCommissionPromotionsExport,
  useFilterCommissionPromotions,
  useUpdateCommissionPromotion,
} from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { AgentTypeTagWithName } from "../../../agent/components/tags/AgentTypeTagWithName";
import { formatAgentIdNumber } from "../../../agent/utils";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { CommissionPromotionStatus, CommissionPromotionType } from "../enums";
import { CommissionPromotion, CommissionPromotionFilterPageRequest } from "../types";

type Props = {
  searchParams: CommissionPromotionFilterPageRequest;
};

export const CommissionPromotionsTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data, isFetching } = useFilterCommissionPromotions({
    ...searchParams,
    promotionTypes: searchParams.promotionTypes?.length ? searchParams.promotionTypes : [CommissionPromotionType.LEVEL],
  });

  const updateCommissionPromotion = useUpdateCommissionPromotion();
  const deleteCommissionPromotion = useDeleteCommissionPromotion();
  const downloadCommissionPromotionsExport = useDownloadCommissionPromotionsExport();

  const columns = useMemo((): ColumnsType<CommissionPromotion> => {
    const items: ColumnsType<CommissionPromotion> = [
      {
        key: "agentName",
        title: t("common.agent"),
        width: 220,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <AgentTypeTagWithName agent={record.agent} hideIdNumber ellipsis showLink showRepresentatives />
        ),
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 100,
        render: (_, record) => formatAgentIdNumber(record.agent),
      },
      {
        key: "status",
        title: (
          <div>
            <div>
              {isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}
            </div>
            {isTopAgentPlus ? <div>{t("agent.helpers.agentSecondaryStatusSFAPlusLabel")}</div> : undefined}
          </div>
        ),
        width: 210,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <>
            <Ellipsis>{t(`agent.enums.agentStatus.${record.agent.status}`)}</Ellipsis>

            {isTopAgentPlus && record.agent.secondaryStatus ? (
              <>
                <br />
                <Ellipsis>{t(`agent.enums.agentSecondaryStatus.${record.agent.secondaryStatus}`)}</Ellipsis>
              </>
            ) : undefined}
          </>
        ),
      },
      {
        key: "status",
        title: t("commissions.promotions.enums.commissionPromotionStatus._label"),
        width: 100,
        render: (_, record) => t(`commissions.promotions.enums.commissionPromotionStatus.${record.status}`),
      },
      {
        key: "date",
        title: t("commissions.promotions.attrs.date"),
        width: 100,
        render: (_, record) => formatLocaleDate(record.date),
      },
    ];

    if (data?.promotionTypes?.[0] === CommissionPromotionType.LEVEL) {
      items.push({
        key: "level",
        title: t("commissions.promotions.enums.commissionPromotionType.LEVEL"),
        children: [
          {
            key: "formerLevel",
            title: t("commissions.promotions.helpers.formerLevel"),
            width: 120,
            ellipsis: { showTitle: false },
            render: (_, record) => (
              <Ellipsis>{record.formerLevel ? formatCommissionsLevelName(record.formerLevel) : undefined}</Ellipsis>
            ),
          },
          {
            key: "nextLevel",
            title: t("commissions.promotions.helpers.nextLevel"),
            width: 120,
            ellipsis: { showTitle: false },
            render: (_, record) => (
              <Ellipsis>{record.nextLevel ? formatCommissionsLevelName(record.nextLevel) : undefined}</Ellipsis>
            ),
          },
        ],
      });
    } else {
      items.push({
        key: "rank",
        title: t("commissions.promotions.enums.commissionPromotionType.RANK"),
        children: [
          {
            key: "formerRank",
            title: t("commissions.promotions.helpers.formerRank"),
            width: 120,
            ellipsis: { showTitle: false },
            render: (_, record) => <Ellipsis>{record.formerRank?.name}</Ellipsis>,
          },
          {
            key: "nextRank",
            title: t("commissions.promotions.helpers.nextRank"),
            width: 120,
            ellipsis: { showTitle: false },
            render: (_, record) => <Ellipsis>{record.nextRank?.name}</Ellipsis>,
          },
        ],
      });
    }

    items.push({
      key: "actions",
      width: 100,
      render: (_, record) => {
        return (
          <Dropdown
            menu={{
              items: [
                record.status === CommissionPromotionStatus.REJECTED
                  ? {
                      key: "pending",
                      label: (
                        <ActionTextIcon
                          color="blue"
                          icon="clock"
                          text={t("common.pending")}
                          onClick={() =>
                            updateCommissionPromotion.mutate({
                              id1: record.agent.id,
                              id2: record.id,
                              object: {
                                optimisticLockVersion: record.optimisticLockVersion,
                                status: CommissionPromotionStatus.PENDING,
                              },
                            })
                          }
                        />
                      ),
                    }
                  : null,
                ...(record.status === CommissionPromotionStatus.PENDING
                  ? [
                      {
                        key: "approve",
                        label: (
                          <Popconfirm
                            title={t("commissions.promotions.helpers.approveConfirm")}
                            icon={<AntIcon type="question" color="blue" />}
                            cancelText={t("common.no")}
                            okText={t("common.yes")}
                            onConfirm={() =>
                              updateCommissionPromotion.mutate({
                                id1: record.agent.id,
                                id2: record.id,
                                object: {
                                  optimisticLockVersion: record.optimisticLockVersion,
                                  status: CommissionPromotionStatus.APPROVED,
                                },
                              })
                            }
                          >
                            <ActionTextIcon color="green" icon="check" text={t("common.approve")} />
                          </Popconfirm>
                        ),
                      },
                      {
                        key: "reject",
                        label: (
                          <ActionTextIcon
                            color="orange"
                            icon="close"
                            text={t("common.reject")}
                            onClick={() =>
                              updateCommissionPromotion.mutate({
                                id1: record.agent.id,
                                id2: record.id,
                                object: {
                                  optimisticLockVersion: record.optimisticLockVersion,
                                  status: CommissionPromotionStatus.REJECTED,
                                },
                              })
                            }
                          />
                        ),
                      },
                    ]
                  : [null]),
                {
                  key: "delete",
                  label: (
                    <Popconfirm
                      title={t("commissions.promotions.helpers.deleteConfirm")}
                      icon={<PopconfirmDeleteIcon />}
                      cancelText={t("common.no")}
                      okText={t("common.yes")}
                      okType="danger"
                      onConfirm={() => deleteCommissionPromotion.mutate({ id1: record.agent.id, id2: record.id })}
                    >
                      <ActionTextIcon color="red" icon="delete" text={t("common.delete")} />
                    </Popconfirm>
                  ),
                },
              ],
            }}
            trigger={["click"]}
          >
            <span>
              <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
            </span>
          </Dropdown>
        );
      },
    });

    return items;
  }, [data]);

  return (
    <Table<CommissionPromotion>
      {...tableStandardProps()}
      title={(data) => {
        if (!data.length) return;

        return (
          <Space>
            <ActionTextIcon
              icon="download"
              color="blue"
              text={t("common.exportXlsx")}
              onClick={() =>
                downloadCommissionPromotionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
              }
            />

            <ActionTextIcon
              icon="download"
              color="green"
              text={t("common.exportCsv")}
              onClick={() =>
                downloadCommissionPromotionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
              }
            />
          </Space>
        );
      }}
      scroll={{ x: TableSizes.LARGE }}
      loading={isFetching}
      columns={columns}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        style: { marginBottom: 0 },
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
